/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { DownloadRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  selectCumulativeUsageByLink,
  selectCumulativeUsageByNetwork,
  selectUsageByNetwork,
  selectUsageByLink,
  selectTotalUsageByNetwork,
  selectTotalUsageByLink,
  selectTotalPacketLossByLink,
  selectServiceAvailability,
} from '../../../store';
import AutocompleteSelect from '../../common/selects/AutocompleteSelect';
import appTheme from '../../common/theme/appTheme.json';
import { ChartExportType, exportChartsToZip, ExportFormatType } from '../DataUsageExport/DataUsageExportUtils';

export default function DownloadAllChartsDialog({
  dataLoaded,
  requestParamsValid,
  fromMsSinceEpoch,
  toMsSinceEpoch,
  refs,
}: {
  dataLoaded: boolean;
  requestParamsValid: number | boolean | null;
  fromMsSinceEpoch: number;
  toMsSinceEpoch: number;
  refs: {
    name: string;
    displayName: string;
    chartExportType: ChartExportType;
    ref: React.RefObject<HTMLDivElement | undefined>;
  }[];
}) {
  const defaultReportName = `UP Reports - ${new Date().toUTCString()}`;

  const [open, setOpen] = useState(false);
  const [reportName, setReportName] = useState<string | undefined | null>(undefined);
  const [selectedExportTypes, setSelectedExportTypes] = useState<ExportFormatType[] | string[]>([]);
  const [selectedExportCharts, setSelectedExportCharts] = useState<ChartExportType[] | string[]>([]);
  const totalUsageByNetwork = useSelector(selectTotalUsageByNetwork, shallowEqual);
  const usageByNetwork = useSelector(selectUsageByNetwork, shallowEqual);
  const totalUsageByLink = useSelector(selectTotalUsageByLink, shallowEqual);
  const usageByLink = useSelector(selectUsageByLink, shallowEqual);
  const cumulativeUsageByLink = useSelector(selectCumulativeUsageByLink, shallowEqual);
  const cumulativeUsageByNetwork = useSelector(selectCumulativeUsageByNetwork, shallowEqual);
  const totalPacketLoss = useSelector(selectTotalPacketLossByLink, shallowEqual);
  const serviceAvailability = useSelector(selectServiceAvailability, shallowEqual);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };
  const handleConfirm = () => {
    window.setTimeout(() => {
      setOpen(false);
    });

    window.setTimeout(() => {
      exportChartsToZip(
        fromMsSinceEpoch,
        toMsSinceEpoch,
        refs,
        selectedExportTypes,
        selectedExportCharts,
        reportName || defaultReportName,
        totalUsageByNetwork,
        usageByNetwork,
        totalUsageByLink,
        usageByLink,
        cumulativeUsageByLink,
        cumulativeUsageByNetwork,
        totalPacketLoss,
        serviceAvailability,
      );
    }, 100);
  };

  return (
    <div>
      <Button
        color="info"
        disabled={!requestParamsValid || !dataLoaded}
        onClick={handleClickOpen}
        sx={{
          backgroundColor: appTheme.mptLightBlue,
          color: '#202E39',
          borderColor: appTheme.mptLightBlue,
          '&:disabled': {
            backgroundColor: appTheme.primaryBackgroundColor,
            color: appTheme.disabledTextColor,
            borderColor: '#202E39',
          },
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            color: '#202E39 !important',
            borderColor: appTheme.mptLightBlue,
            svg: {
              color: '#202E39 !important',
            },
          },
        }}
        endIcon={<DownloadRounded />}
        variant="contained"
      >
        Download Reports
      </Button>
      <Dialog
        open={open}
        onClose={handleCancel}
        sx={{ overflow: 'visible', '.MuiDialog-paper': { overflowY: 'visible' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '32px', lineHeight: '40px', letterSpacing: '0.2px' }}>
          Download reports
        </DialogTitle>
        <Box minHeight="324px" minWidth="348px" sx={{ overflow: 'visible' }}>
          <DialogContent>
            <Box>
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: 0,
                  height: 24,
                  color: appTheme.secondaryTextColor,
                }}
              >
                Filename
              </Box>
              <TextField
                sx={{
                  '* > input': {
                    color: '#ffffff',
                  },
                }}
                autoFocus
                id="filename"
                type="text"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  hidden: true,
                }}
                variant="outlined"
                value={reportName}
                placeholder={defaultReportName}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  setReportName(event.target.value);
                }}
              />
            </Box>
            <Box marginTop="24px">
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: 0,
                  height: 24,
                  color: appTheme.secondaryTextColor,
                }}
              >
                Select file type(s)
              </Box>
              <Box sx={{ width: '300px' }}>
                <AutocompleteSelect<string>
                  onSelectboxValueChanged={(values: string[]) => {
                    setSelectedExportTypes(values);
                  }}
                  getOptionLabel={(label: string) => label}
                  isOptionEqualToValue={(option: string, value: string) => {
                    return option === value;
                  }}
                  options={['PowerPoint (.ppt)', 'Image (.png)', 'PDF (.pdf)'] as ExportFormatType[]}
                  placeholderText="Select file type(s)"
                  multipleSelectedText="Multiple file types selected"
                  popperWidth="300px"
                />
              </Box>
            </Box>
            <Box marginTop="24px">
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: 0,
                  height: 24,
                  color: appTheme.secondaryTextColor,
                }}
              >
                Select report(s)
              </Box>
              <Box sx={{ width: '300px', position: 'absolute' }}>
                <AutocompleteSelect<string>
                  onSelectboxValueChanged={(values: string[]) => {
                    setSelectedExportCharts(values);
                  }}
                  getOptionLabel={(label: string) => label}
                  isOptionEqualToValue={(option: string, value: string) => {
                    return option === value;
                  }}
                  options={
                    [
                      'Link Usage',
                      'Link Usage (cumulative)',
                      'Link Usage (comparison)',
                      'Network Usage',
                      'Network Usage (cumulative)',
                      'Network Usage (comparison)',
                      'Performance Summary',
                      'Service Availability'
                    ] as ChartExportType[]
                  }
                  placeholderText="Select Report(s)"
                  multipleSelectedText="Multiple reports selected"
                  popperWidth="300px"
                />
              </Box>
            </Box>
          </DialogContent>
        </Box>
        <DialogActions sx={{ minHeight: '102px', padding: '8px', borderWidth: '0px' }} disableSpacing>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingX: '16px' }}>
            <Button
              sx={{
                height: '40px',
                backgroundColor: appTheme.primaryBackgroundColor,
                color: '#ffffff',
                borderColor: '#ffffff',
              }}
              variant="contained"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedExportTypes?.length || !selectedExportCharts?.length}
              sx={{
                height: '40px',
                backgroundColor: appTheme.mptLightBlue,
                color: '#202E39',
                borderColor: appTheme.mptLightBlue,
                '* > *': {
                  '&:hover': {
                    backgroundColor: appTheme.mptLightBlue,
                    color: '#202E39',
                    borderColor: appTheme.mptLightBlue,
                  },
                },
                '&:hover': {
                  backgroundColor: appTheme.mptLightBlue,
                  color: '#202E39',
                  borderColor: appTheme.mptLightBlue,
                },
                '&:disabled': {
                  backgroundColor: appTheme.primaryBackgroundColor,
                  color: appTheme.disabledTextColor,
                  borderColor: appTheme.disabledTextColor,
                },
              }}
              variant="contained"
              onClick={handleConfirm}
            >
              Download Reports
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}
